import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import Header, { BreadcrumbItem } from '../components/Header';
import { usePageTracking } from '../hooks/usePageTracking';
import { useAuth } from '../context/AuthContext';
import {
  meetingService,
  MeetingMemberStatus,
  MeetingMemberPreferences,
} from '../services/meetingService';
import {
  loadWorkshop2026Context,
  getWorkshop2026MeetingCodeFromSite,
  fetchWorkshop2026Meeting,
  formatWorkshopSchedule,
  getWorkshop2026DisplayName,
  formatFirstSessionDate,
  WORKSHOP_2026_PATH,
} from '../services/workshop2026Service';
import { ProfileService } from '../services/profileService';
import { Meeting } from '../services/meetingService';
import Workshop2026AboutTab from '../components/workshop2026/Workshop2026AboutTab';
import Workshop2026WorkshopTab from '../components/workshop2026/Workshop2026WorkshopTab';
import WorkshopInterestModal from '../components/workshop2026/WorkshopInterestModal';
import { interestSurveyToPayload } from '../data/workshop2026InterestSurvey';
import { useDialog } from '../context/DialogContext';
import { useProfileModal } from '../context/ProfileModalContext';
import { useLocale } from '../context/LocaleContext';
import { perfMark, perfSummary, perfTimeAsync } from '../utils/perfTimer';
import styles from '../styles/workshop2026Page.module.css';

type WorkshopTab = 'about' | 'workshop';

const DEFAULT_PREFERENCES: MeetingMemberPreferences = {
  include_on_printed_phone_list: false,
  include_email_on_list: false,
  name_list_mode: 'when_workshop_starts',
  sponsor_willingness: 'no',
  display_preference: 'name_only',
  email_notifications_enabled: true,
};

interface Workshop2026PageProps {
  user?: any;
  onLogout: () => void;
  onLogin: () => void;
  onCreateAccount?: () => void;
}

const Workshop2026Page: React.FC<Workshop2026PageProps> = ({
  user,
  onLogout,
  onLogin,
  onCreateAccount,
}) => {
  const { user: authUser } = useAuth();
  const { showConfirm, showAlert } = useDialog();
  const { isProfileModalOpen } = useProfileModal();
  const { t } = useLocale();
  const profileModalWasOpen = useRef(false);
  const effectiveUser = user || authUser;
  const [searchParams, setSearchParams] = useSearchParams();
  const [shellReady, setShellReady] = useState(false);
  const [meetingLoading, setMeetingLoading] = useState(true);
  const [meeting, setMeeting] = useState<Meeting | null>(null);
  const [meetingCode, setMeetingCode] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [memberStatus, setMemberStatus] = useState<MeetingMemberStatus | null>(null);
  const [profilePhone, setProfilePhone] = useState('');
  const [interestModalOpen, setInterestModalOpen] = useState(false);
  const [withdrawingInterest, setWithdrawingInterest] = useState(false);
  const [confirmingAttendance, setConfirmingAttendance] = useState(false);

  const getInitialTab = (): WorkshopTab => {
    const t = searchParams.get('tab');
    if (t === 'workshop') return 'workshop';
    return 'about';
  };

  const [activeTab, setActiveTab] = useState<WorkshopTab>(getInitialTab());

  usePageTracking('2026 SAA Step Workshop');

  useEffect(() => {
    const tabFromUrl = getInitialTab();
    if (tabFromUrl !== activeTab) {
      setActiveTab(tabFromUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const refreshMeeting = useCallback(async (authed = false) => {
    if (meetingCode) {
      setMeetingLoading(true);
      const result = await fetchWorkshop2026Meeting(meetingCode, { authed });
      setMeeting(result.meeting);
      setLoadError(result.loadError);
      setMeetingLoading(false);
      return { meeting: result.meeting, meetingCode, loadError: result.loadError };
    }
    const ctx = await loadWorkshop2026Context({ authed });
    setMeeting(ctx.meeting);
    setMeetingCode(ctx.meetingCode);
    setLoadError(ctx.loadError);
    setMeetingLoading(false);
    return ctx;
  }, [meetingCode]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      perfMark('Workshop2026Page', 'initial load start');
      const code = await perfTimeAsync('Workshop2026Page site config (shell)', () =>
        getWorkshop2026MeetingCodeFromSite()
      );
      if (cancelled) return;
      setMeetingCode(code);
      setShellReady(true);
      perfMark('Workshop2026Page', 'shell visible');
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    if (!shellReady) return;

    if (!meetingCode) {
      setMeeting(null);
      setLoadError(null);
      setMeetingLoading(false);
      return;
    }

    (async () => {
      setMeetingLoading(true);
      const result = await perfTimeAsync('Workshop2026Page load meeting (background)', () =>
        fetchWorkshop2026Meeting(meetingCode, { authed: !!effectiveUser })
      );
      if (cancelled) return;
      setMeeting(result.meeting);
      setLoadError(result.loadError);
      setMeetingLoading(false);
      perfMark('Workshop2026Page', 'meeting loaded');
    })();

    return () => {
      cancelled = true;
    };
  }, [shellReady, meetingCode, effectiveUser]);

  const refreshMemberStatus = useCallback(async () => {
    if (!effectiveUser || !meetingCode) {
      setMemberStatus(null);
      return;
    }
    try {
      const status = await meetingService.getMemberStatus(meetingCode);
      setMemberStatus(status);
    } catch {
      setMemberStatus(null);
    }
  }, [effectiveUser, meetingCode]);

  useEffect(() => {
    refreshMemberStatus();
  }, [refreshMemberStatus]);

  useEffect(() => {
    if (!effectiveUser) {
      setProfilePhone('');
      return;
    }
    ProfileService.getUserProfile()
      .then((p) => setProfilePhone(p.phone || ''))
      .catch(() => setProfilePhone(''));
  }, [effectiveUser]);

  useEffect(() => {
    if (profileModalWasOpen.current && !isProfileModalOpen && effectiveUser) {
      ProfileService.getUserProfile()
        .then((p) => setProfilePhone(p.phone || ''))
        .catch(() => setProfilePhone(''));
    }
    profileModalWasOpen.current = isProfileModalOpen;
  }, [isProfileModalOpen, effectiveUser]);

  // Log API call summary once shell and meeting fetch have settled.
  useEffect(() => {
    if (shellReady && !meetingLoading) {
      perfSummary(`Workshop2026Page (${activeTab} tab)`);
    }
  }, [shellReady, meetingLoading, meetingCode, memberStatus, activeTab]);

  const handleTabClick = (tab: WorkshopTab) => {
    perfMark('Workshop2026Page', `tab → ${tab}`);
    setActiveTab(tab);
    setSearchParams({ tab });
  };

  const handleJoinInterest = () => {
    if (!meetingCode) return;
    setInterestModalOpen(true);
  };

  const handleInterestSubmit = async (payload: ReturnType<typeof interestSurveyToPayload>) => {
    if (!meetingCode) throw new Error('Workshop not configured');
    const result = await meetingService.registerWorkshopInterest(meetingCode, payload);
    await refreshMemberStatus();
    await refreshMeeting(true);
    setActiveTab('workshop');
    setSearchParams({ tab: 'workshop' });
    const email = effectiveUser?.email || '';
    const emailNote =
      result.confirmation_email_sent && email
        ? t('workshop.2026.alert.interest_email_sent', { email })
        : email
          ? t('workshop.2026.alert.interest_email_keep', { email })
          : t('workshop.2026.alert.interest_email_generic');
    showAlert({
      title: t('workshop.2026.alert.interest_title'),
      message: `${t('workshop.2026.alert.interest_body')}${emailNote}`,
      variant: 'success',
    });
  };

  const handleConfirmAttendance = () => {
    if (!meetingCode || confirmingAttendance) return;
    showConfirm({
      title: t('workshop.2026.dialog.confirm_title'),
      message: t('workshop.2026.dialog.confirm_message'),
      confirmText: t('workshop.2026.dialog.confirm_ok'),
      cancelText: t('workshop.2026.dialog.confirm_cancel'),
      variant: 'info',
      onConfirm: async () => {
        setConfirmingAttendance(true);
        try {
          const result = await meetingService.confirmWorkshopAttendance(meetingCode);
          await refreshMemberStatus();
          const ctx = await refreshMeeting(true);
          setActiveTab('workshop');
          setSearchParams({ tab: 'workshop' });
          const email = effectiveUser?.email || '';
          const emailNote =
            result.confirmation_email_sent && email
              ? t('workshop.2026.alert.interest_email_sent', { email })
              : '';
          showAlert({
            title: t('workshop.2026.alert.confirmed_title'),
            message: t('workshop.2026.alert.confirmed_body', {
              date: formatFirstSessionDate(ctx.meeting),
              emailNote,
            }),
            variant: 'success',
          });
        } catch (err) {
          showAlert({
            message: err instanceof Error ? err.message : t('workshop.2026.alert.confirm_error'),
            variant: 'danger',
          });
        } finally {
          setConfirmingAttendance(false);
        }
      },
    });
  };

  const handleSavePreferences = async (updates: Partial<MeetingMemberPreferences>) => {
    if (!meetingCode) return;
    const saved = await meetingService.updateMyMemberPreferences(meetingCode, updates);
    setMemberStatus((prev) => (prev ? { ...prev, ...saved } : prev));
  };

  const handleSaveProfilePhone = async (phone: string) => {
    await ProfileService.updateUserProfile({ phone });
    setProfilePhone(phone);
  };

  const handleLeaveWorkshop = () => {
    if (!meetingCode || withdrawingInterest) return;
    const isConfirmed = memberStatus?.registration_state === 'confirmed';
    showConfirm({
      title: isConfirmed
        ? t('workshop.2026.dialog.leave_title_confirmed')
        : t('workshop.2026.dialog.leave_title_interest'),
      message: isConfirmed
        ? t('workshop.2026.dialog.leave_message_confirmed')
        : t('workshop.2026.dialog.leave_message_interest'),
      confirmText: isConfirmed
        ? t('workshop.2026.dialog.leave_ok_confirmed')
        : t('workshop.2026.dialog.leave_ok_interest'),
      cancelText: isConfirmed
        ? t('workshop.2026.dialog.leave_cancel_confirmed')
        : t('workshop.2026.dialog.leave_cancel_interest'),
      variant: 'danger',
      onConfirm: async () => {
        setWithdrawingInterest(true);
        try {
          await meetingService.withdrawWorkshopInterest(meetingCode);
          await refreshMemberStatus();
          await refreshMeeting(true);
        } catch (err) {
          showAlert({
            message: err instanceof Error ? err.message : t('workshop.2026.alert.leave_error'),
            variant: 'danger',
          });
        } finally {
          setWithdrawingInterest(false);
        }
      },
    });
  };

  const breadcrumbs: BreadcrumbItem[] = [
    { label: t('nav.home'), path: '/', active: false },
    { label: t('nav.saa'), path: '/saa', active: false },
    { label: t('workshop.2026.nav_label'), path: WORKSHOP_2026_PATH, active: true },
  ];

  const scheduleLine = formatWorkshopSchedule(meeting);
  const firstSession = formatFirstSessionDate(meeting);
  const displayName = getWorkshop2026DisplayName(meeting);
  const showAdminConfigWarning = !!(effectiveUser?.isAdmin || memberStatus?.is_admin);
  const userDisplayName =
    effectiveUser?.name || effectiveUser?.display_name || effectiveUser?.displayName || '';

  const registrationState = memberStatus?.registration_state ?? 'none';
  const memberPreferences: MeetingMemberPreferences = memberStatus
    ? {
        include_on_printed_phone_list: memberStatus.include_on_printed_phone_list,
        include_email_on_list: memberStatus.include_email_on_list,
        name_list_mode: memberStatus.name_list_mode,
        sponsor_willingness: memberStatus.sponsor_willingness,
        display_preference: memberStatus.display_preference,
        email_notifications_enabled: memberStatus.email_notifications_enabled,
      }
    : DEFAULT_PREFERENCES;

  if (!shellReady) {
    return (
      <div className={styles.workshopPage}>
        <Header breadcrumbs={breadcrumbs} user={effectiveUser} onLogout={onLogout} onLogin={onLogin} />
        <main className={styles.mainContent}>
          <div className={styles.loading}>{t('workshop.2026.loading')}</div>
        </main>
      </div>
    );
  }

  return (
    <div className={styles.workshopPage}>
      <Header breadcrumbs={breadcrumbs} user={effectiveUser} onLogout={onLogout} onLogin={onLogin} />

      <main className={styles.mainContent}>
        {showAdminConfigWarning && !meetingCode && (
          <div className={styles.configWarning}>
            Workshop backend not linked yet. Set the meeting code in Admin → Home
            (e.g. after creating a workshop record).
          </div>
        )}
        {showAdminConfigWarning && loadError && (
          <div className={styles.configWarning}>
            Could not load workshop record ({meetingCode}): {loadError}
          </div>
        )}

        <section className={styles.hero}>
          <h1 className={styles.heroTitle}>{displayName}</h1>
          <p className={styles.heroSubtitle}>{t('workshop.2026.hero_subtitle')}</p>
          <p className={styles.heroSchedule}>
            {meetingLoading && !meeting ? (
              <>{t('workshop.2026.loading_schedule')}</>
            ) : (
              <>
                {scheduleLine} · {t('workshop.2026.starting', { date: firstSession })}
              </>
            )}
          </p>
          {meeting?.description && (
            <p className={styles.heroSchedule} style={{ marginTop: '0.4rem' }}>
              {meeting.description}
            </p>
          )}
        </section>

        {effectiveUser && registrationState === 'confirmed' && (
          <div className={styles.statusStrip}>
            <p className={styles.statusStripText}>{t('workshop.2026.status_confirmed')}</p>
            <p className={styles.statusStripSub}>
              {t('workshop.2026.status_first_session', { date: firstSession })}
            </p>
          </div>
        )}

        <div className={styles.tabBar}>
          <button
            type="button"
            className={`${styles.tab} ${activeTab === 'about' ? styles.activeTab : ''}`}
            onClick={() => handleTabClick('about')}
          >
            {t('workshop.2026.tab_about')}
          </button>
          <button
            type="button"
            className={`${styles.tab} ${activeTab === 'workshop' ? styles.activeTab : ''}`}
            onClick={() => handleTabClick('workshop')}
          >
            {t('workshop.2026.tab_workshop')}
          </button>
        </div>

        <div className={styles.tabPanel}>
          {meetingLoading && activeTab === 'workshop' && effectiveUser && meetingCode && (
            <p className={styles.helperText}>{t('workshop.2026.loading_details')}</p>
          )}
          {activeTab === 'about' && (
            <Workshop2026AboutTab
              isSignedIn={!!effectiveUser}
              registrationState={registrationState}
              firstSessionLabel={firstSession}
              meeting={meeting}
              onGoToWorkshopTab={() => handleTabClick('workshop')}
              onLoginForQuestion={onLogin}
              userEmail={effectiveUser?.email}
              userDisplayName={userDisplayName}
            />
          )}
          {activeTab === 'workshop' && (
            <>
              {!effectiveUser ? (
                <div className={styles.workshopJoinBlock}>
                  <p className={styles.workshopJoinLead}>{t('workshop.2026.gate.lead')}</p>
                  <div className={styles.statusActions}>
                    {onCreateAccount && (
                      <button type="button" className={styles.primaryBtn} onClick={onCreateAccount}>
                        {t('workshop.2026.gate.create_account')}
                      </button>
                    )}
                    <button type="button" className={styles.secondaryBtn} onClick={onLogin}>
                      {t('workshop.2026.gate.sign_in')}
                    </button>
                  </div>
                  <p className={styles.workshopJoinDetail}>{t('workshop.2026.gate.detail')}</p>
                </div>
              ) : (
                <Workshop2026WorkshopTab
                  registrationState={registrationState}
                  meeting={meeting}
                  meetingCode={meetingCode}
                  memberStatus={memberStatus}
                  interestAt={memberStatus?.interest_at}
                  memberPreferences={memberPreferences}
                  profilePhone={profilePhone}
                  user={effectiveUser}
                  userEmail={effectiveUser?.email}
                  workshopEngagedAt={
                    registrationState === 'interest' || registrationState === 'confirmed'
                      ? memberStatus?.interest_at || memberStatus?.confirmed_at || null
                      : null
                  }
                  onJoinInterestList={handleJoinInterest}
                  onConfirmAttendance={handleConfirmAttendance}
                  onLeaveWorkshop={handleLeaveWorkshop}
                  onSavePreferences={handleSavePreferences}
                  onSavePhone={handleSaveProfilePhone}
                  confirming={confirmingAttendance}
                  leaving={withdrawingInterest}
                  meetingCodeMissing={!meetingCode}
                />
              )}
            </>
          )}
        </div>

        {effectiveUser && memberStatus?.is_admin && meetingCode && (
          <div className={styles.adminStripBottom}>
            <span>You&apos;re the workshop admin.</span>
            <a href={`/meetings/${meetingCode}/settings`} className={styles.adminStripLink}>
              Workshop settings →
            </a>
          </div>
        )}
      </main>

      <WorkshopInterestModal
        isOpen={interestModalOpen}
        onClose={() => setInterestModalOpen(false)}
        onSubmit={handleInterestSubmit}
      />
    </div>
  );
};

export default Workshop2026Page;
